import React, { useState } from 'react'
import QRCodeGenerator from '../common/QRCodeGenerator'
import Avatar from '../common/Avatar'

interface ShareInviteModalProps {
  isOpen: boolean
  onClose: () => void
  inviteCode: string
  inviteUrl: string
  caseId?: string // ID del caso para generar avatar consistente
  caseInfo: {
    name: string
    profileName: string
    profileType: 'human' | 'pet'
    avatar?: string
    avatarCustomization?: any
    petAvatarConfig?: any
  }
  inviteType: 'caregiver' | 'patient'
}

const ShareInviteModal: React.FC<ShareInviteModalProps> = ({
  isOpen,
  onClose,
  inviteCode,
  inviteUrl,
  caseId,
  caseInfo,
  inviteType
}) => {
  const [copiedText, setCopiedText] = useState<string>('')

  if (!isOpen) return null

  // Crear mensaje personalizado
  const createMessage = (format: 'simple' | 'detailed' = 'simple') => {
    const roleText = inviteType === 'caregiver' ? 'cuidador' : 'paciente'
    const profileIcon = caseInfo.profileType === 'human' ? '👤' : '🐾'
    
    if (format === 'simple') {
      return `¡Hola! Te invito a unirte a un caso de medicamentos ${profileIcon}

Código: ${inviteCode}
Enlace: ${inviteUrl}

¡Esperamos contar contigo como ${roleText}!`
    } else {
      return `🏥 Invitación a Mi Medicación

Te invito a unirte al caso: "${caseInfo.name}"
Paciente: Información disponible al unirse ${profileIcon}
Rol: ${roleText === 'cuidador' ? 'Cuidador' : 'Paciente'}

🔑 Código de invitación: ${inviteCode}
🔗 Enlace directo: ${inviteUrl}

Con esta aplicación podrás:
• Gestionar medicamentos
• Recibir recordatorios
• Hacer seguimiento de tratamientos
• Colaborar con familiares y cuidadores

¡Esperamos contar contigo!`
    }
  }

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedText(label)
      setTimeout(() => setCopiedText(''), 2000)
    } catch (err) {
      console.error('Error copying to clipboard:', err)
    }
  }

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(createMessage('simple'))
    const whatsappUrl = `https://wa.me/?text=${message}`
    window.open(whatsappUrl, '_blank')
  }

  // const shareViaTelegram = () => {
  //   const message = encodeURIComponent(createMessage('simple'))
  //   const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(inviteUrl)}&text=${message}`
  //   window.open(telegramUrl, '_blank')
  // }

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Invitación a Mi Medicación - ${caseInfo.name}`)
    const body = encodeURIComponent(createMessage('detailed'))
    const emailUrl = `mailto:?subject=${subject}&body=${body}`
    window.open(emailUrl)
  }

  const shareViaSMS = () => {
    const message = encodeURIComponent(createMessage('simple'))
    const smsUrl = `sms:?body=${message}`
    window.open(smsUrl)
  }

  const shareViaWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Invitación a Mi Medicación - ${caseInfo.name}`,
          text: createMessage('simple'),
          url: inviteUrl
        })
      } catch (err) {
        console.error('Error sharing via Web API:', err)
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Compartir Invitación
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Información del caso */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Avatar
                user={{
                  type: caseInfo.profileType === 'pet' ? 'pet' : 'human',
                  name: caseInfo.profileName || caseInfo.name || 'Caso',
                  userId: caseId || inviteCode, // Usar caseId como identidad estable, o inviteCode como fallback
                  avatarUrl: caseInfo.avatar,
                  avatarCustomization: caseInfo.avatarCustomization,
                  petAvatarConfig: caseInfo.petAvatarConfig
                }}
                size="md"
              />
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  {caseInfo.name}
                </h3>
                <p className="text-sm text-blue-600 dark:text-blue-300">
                  Caso de cuidado • Invitación para {inviteType === 'caregiver' ? 'cuidador' : 'paciente'}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Códigos y Enlaces */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Código de Invitación
              </h3>
              
              {/* Código corto */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Código:</span>
                  {copiedText === 'code' && (
                    <span className="text-sm text-green-600 dark:text-green-400">¡Copiado!</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-lg font-mono font-bold text-blue-600 dark:text-blue-400">
                    {inviteCode}
                  </code>
                  <button
                    onClick={() => copyToClipboard(inviteCode, 'code')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Enlace completo */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Enlace:</span>
                  {copiedText === 'url' && (
                    <span className="text-sm text-green-600 dark:text-green-400">¡Copiado!</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                    {inviteUrl}
                  </code>
                  <button
                    onClick={() => copyToClipboard(inviteUrl, 'url')}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Mensaje formateado */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Mensaje completo:</span>
                  {copiedText === 'message' && (
                    <span className="text-sm text-green-600 dark:text-green-400">¡Copiado!</span>
                  )}
                </div>
                <div className="flex items-start gap-2">
                  <pre className="flex-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {createMessage('simple')}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(createMessage('simple'), 'message')}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Código QR */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Código QR
              </h3>
              
              <div className="flex justify-center">
                <QRCodeGenerator
                  value={inviteUrl}
                  size={200}
                  title="Escanea para unirte"
                />
              </div>
            </div>
          </div>

          {/* Botones de compartir */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Compartir directamente
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={shareViaWhatsApp}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.785"/>
                </svg>
                WhatsApp
              </button>

              <button
                onClick={shareViaEmail}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </button>

              <button
                onClick={shareViaSMS}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                SMS
              </button>

              {typeof window !== 'undefined' && 'share' in navigator && (
                <button
                  onClick={shareViaWebAPI}
                  className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                  Compartir
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShareInviteModal